import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import '@/styles/ipe/workbench/ipe-workbench.css';
import '@/styles/shared/modal-footer-confirm-button.css';

// Toast 提示组件
function Toast({ message, type = 'info', durationMs = 3000, manualDismiss = false, onClose }) {
  useEffect(() => {
    if (!onClose || manualDismiss) return;
    const ms = typeof durationMs === 'number' && durationMs > 0 ? durationMs : 3000;
    const timer = setTimeout(() => {
      try {
        onClose();
      } catch (error) {
        console.error('Toast onClose 执行失败:', error);
      }
    }, ms);

    return () => {
      clearTimeout(timer);
    };
  }, [onClose, durationMs, manualDismiss]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" fill="#4caf50"/>
            <path d="M6 10l2 2 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" fill="#f44336"/>
            <path d="M10 6v4M10 14h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 2L2 18h16L10 2z" fill="#ff9800"/>
            <path d="M10 8v4M10 14h.01" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" fill="#2196f3"/>
            <path d="M10 6v4M10 14h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
    }
  };

  return (
    <div className={`modern-toast modern-toast--${type}${manualDismiss ? ' modern-toast--manual-dismiss' : ''}`}>
      <div className="modern-toast__icon">
        {getIcon()}
      </div>
      <span className="modern-toast__message">{message}</span>
      {manualDismiss && (
        <button type="button" className="modern-toast__dismiss" onClick={onClose}>
          知道了
        </button>
      )}
    </div>
  );
}

/**
 * 平台/网络类错误文案的特征：即便调用方误传 success，也不该显示成绿色成功。
 * 只匹配「错误码带上下文」的写法，避免把正常的数量/编号误判。
 */
const ERROR_LIKE_TEXT = /(平台\s*code|errorCode|error_code|错误码)\s*[=:：]?\s*\d{4,}|无法连接|网关不可达|连接超时|请求超时|网络异常|access_token\s+(not exists|is expired|invalid)|timed?\s*out|Network Error/i;

/**
 * 显示 Toast
 * @param {string} message
 * @param {'info'|'success'|'warning'|'error'} [type='info']
 * @param {number|{ manualDismiss?: boolean, durationMs?: number }} [durationMsOrOpts=3000] 停留毫秒数；传 `{ manualDismiss: true }` 表示仅点「知道了」关闭（顶部、无自动消失）
 */
export function showToast(message, type = 'info', durationMsOrOpts = 3000) {
  const text = typeof message === 'string' || typeof message === 'number'
    ? String(message || '').trim()
    : '';
  // 图片读图/签名限流属于正常排队节流，不弹错误提示
  if (text && /操作过于频繁|请求过于频繁|TOO_MANY_REQUESTS|\b429\b|系统繁忙/i.test(text)) {
    return;
  }
  // 兜底：内容明显是错误，却传了 success，按 error 展示（只有真正成功才允许绿色）
  const safeType = type === 'success' && text && ERROR_LIKE_TEXT.test(text) ? 'error' : type;

  let durationMs = 3000;
  let manualDismiss = false;
  if (durationMsOrOpts != null && typeof durationMsOrOpts === 'object' && !Array.isArray(durationMsOrOpts)) {
    manualDismiss = !!durationMsOrOpts.manualDismiss;
    if (typeof durationMsOrOpts.durationMs === 'number') {
      durationMs = durationMsOrOpts.durationMs;
    } else {
      durationMs = manualDismiss ? 0 : 3000;
    }
  } else if (typeof durationMsOrOpts === 'number' && !Number.isNaN(durationMsOrOpts)) {
    durationMs = durationMsOrOpts;
  }

  // 新 toast 顶替旧的，避免连点/双触发时两个成功提示叠在同一位置
  document.querySelectorAll('#toast-container').forEach((node) => {
    try {
      node.remove();
    } catch {
      /* ignore */
    }
  });

  const container = document.createElement('div');
  container.id = 'toast-container';
  document.body.appendChild(container);

  const root = createRoot(container);

  const closeToast = () => {
    try {
      root.unmount();
    } catch {
      /* ignore */
    }
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // 渲染Toast
  root.render(
    <Toast
      message={message}
      type={safeType}
      durationMs={durationMs}
      manualDismiss={manualDismiss}
      onClose={closeToast}
    />,
  );

  return { close: closeToast };
}

function confirmToastIcon(type) {
  switch (type) {
    case 'success':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="9" fill="#4caf50" />
          <path d="M6 10l2 2 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
    case 'error':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="9" fill="#f44336" />
          <path d="M10 6v4M10 14h.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'warning':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 2L2 18h16L10 2z" fill="#ff9800" />
          <path d="M10 8v4M10 14h.01" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="9" fill="#2196f3" />
          <path d="M10 6v4M10 14h.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
  }
}

/** 顶部 Toast 风格确认框：无自动关闭、点遮罩不关闭，仅能通过按钮关闭；resolve 为 true=确定 / false=取消 */
function ConfirmToastHost({ message, type, confirmText, cancelText, onConfirm, onCancel }) {
  return (
    <div className="modern-toast-confirm-host" role="presentation">
      <div
        className={`modern-toast modern-toast--confirm modern-toast--${type}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modern-toast-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modern-toast--confirm__body">
          <div className="modern-toast__icon">{confirmToastIcon(type)}</div>
          <div id="modern-toast-confirm-title" className="modern-toast__message modern-toast--confirm__message">
            {message}
          </div>
        </div>
        <div className="modern-toast--confirm__actions">
          <button type="button" className="modern-toast--confirm__btn modern-toast--confirm__btn--cancel" onClick={onCancel}>
            {cancelText}
          </button>
          <button type="button" className="modern-toast--confirm__btn modern-toast--confirm__btn--primary" onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export function showConfirmToast({
  message,
  type = 'warning',
  confirmText = '确定',
  cancelText = '取消',
}) {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    const cleanup = () => {
      root.unmount();
      if (container.parentNode) container.parentNode.removeChild(container);
    };
    const finish = (value) => {
      cleanup();
      resolve(value);
    };
    root.render(
      <ConfirmToastHost
        message={message}
        type={type}
        confirmText={confirmText}
        cancelText={cancelText}
        onCancel={() => finish(false)}
        onConfirm={() => finish(true)}
      />,
    );
  });
}
